import { Component, HostBinding, inject, Input, signal, Signal, ViewChild } from '@angular/core';
import {
  ActiveLookup, AgentKeys,
  Association,
  AssociationKeys,
  BrokenRule,
  ChangeSourceType,
  Constants,
  DietaryConsiderationKeys,
  LayoutGridType,
  Lookup,
  LookupKeys,
  Lookups,
  Messages,
  TShirtSizesKeys,
} from '@ag-common-lib/public-api';
import { BehaviorSubject } from 'rxjs';
import { AgentAssociationsService } from '../../../../services/agent-associations.service';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { ModalWindowComponent } from '../../../modal-window/modal-window.component';
import DataSource from 'devextreme/data/data_source';
import ArrayStore from 'devextreme/data/array_store';
import { AssociationFormService } from './association-form.service';
import { DxDataGridComponent, DxFormComponent, DxTabPanelComponent } from 'devextreme-angular';
import { LookupsService } from 'ag-common-svc/lib/services';
import { matcher } from '../../../../utils/phone-number-data.utils';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { validateSingleDxGroup } from 'ag-common-svc/lib/utils/validation';
import {
  ASSOCIATION_FORM_TABS,
  AssociationFormSections,
} from 'ag-common-svc/lib/components/agent-editor/components/associations/associations-form.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { ChangeSourceTypeService } from 'ag-common-svc/lib/services/change-source-type.service';

@Component({
  selector: 'ag-shr-associations',
  templateUrl: './associations.component.html',
  styleUrls: ['./associations.component.scss'],
  providers: [AssociationFormService],
})
export class AssociationsComponent {
  @HostBinding('class') className = 'associations';

  @Input() set agentId(value) {
    this.agentId$.next(value);
    this.agentDbId = value ?? null;
  }

  @Input() hasPermissionsToEdit: boolean = true;
  @Input() hasPermissionsToDelete: boolean = true;
  @Input() hasPermissionsToAdd: boolean = true;
  @Input() gridTitle: string = 'Associations';
  @Input() type: LayoutGridType = LayoutGridType.short;
  @ViewChild('associationFormRef', { static: false }) associationFormComponent: DxFormComponent;
  @ViewChild('associationModalRef', { static: true }) associationModalComponent: ModalWindowComponent;
  @ViewChild('associationsDataGridRef', { static: false }) associationsDataGrid: DxDataGridComponent;
  @ViewChild('associationsTabsRef', { static: false }) associationsTabsComponent: DxTabPanelComponent;

  private changeSourceTypeService = inject(ChangeSourceTypeService);

  private _sourceType: ChangeSourceType = this.changeSourceTypeService.sourceType();

  associationModalTitle = 'Add / Edit Association';
  readonly Messages = Messages;
  readonly totalPhoneDigits: number = Constants.TOTAL_PHONE_DIGITS;
  readonly phoneMask: string = Constants.PHONE_MASK;

  protected errors = signal<BrokenRule<AssociationFormSections>[]>([]);
  inProgress: Signal<boolean>;
  inEditMode = signal<boolean>(false);

  associations: Signal<DataSource>;
  associationFormData: Association;
  relationshipTypeLookup: Signal<ActiveLookup[]>;
  selectedRowKeys: string[] = [];

  readonly BaseModelKeys = BaseModelKeys;
  readonly LookupKeys = LookupKeys;
  readonly TShirtSizesKeys = TShirtSizesKeys;
  readonly DietaryConsiderationKeys = DietaryConsiderationKeys;
  readonly AssociationKeys = AssociationKeys;
  readonly emptyMessage = 'No Associations Currently Exist';
  readonly dateFormat: string = Constants.DISPLAY_DATE_FORMAT;

  protected readonly tabsDataSource = ASSOCIATION_FORM_TABS;

  protected readonly mainInfoValidationGroup = 'mainInfoValidationGroup';
  protected readonly funStuffSizesValidationGroup = 'funStuffSizesValidationGroup';
  protected readonly addressValidationGroup = 'addressValidationGroup';
  protected readonly flightPreferenceValidationGroup = 'flightPreferenceValidationGroup';
  protected readonly dietaryConsiderationsValidationGroup = 'dietaryConsiderationsValidationGroup';

  protected readonly LayoutGridType = LayoutGridType;
  protected readonly Lookups = Lookups;

  private readonly agentId$ = new BehaviorSubject<string>(undefined);
  public agentDbId: string | null = null;

  constructor(
    private readonly lookupsService: LookupsService,
    private readonly associationFormService: AssociationFormService,
    private readonly agentAssociationsService: AgentAssociationsService,
  ) {
    this.relationshipTypeLookup = toSignal(this.lookupsService.associationTypeLookup$);
    this.inProgress = toSignal(this.associationFormService.inProgress$, { initialValue: false });

    this.associations = toSignal(
      this.agentId$.pipe(
        filter(Boolean),
        switchMap((agentId: string) => this.agentAssociationsService.getList(agentId)),
        map(associations => {
          return new DataSource({
            store: new ArrayStore({
              key: 'dbId',
              data: Array.isArray(associations) ? associations : [],
            }),
          });
        }),
        shareReplay(1),
      ),
    );
  }

  showAddAssociationPopup = async (): Promise<void> => {
    this.associationModalTitle = 'Add Association';
    this.associationFormData = this.associationFormService.getFormData();
    this.associationModalComponent.showModal();
  };

  showEditPopup = async ({ row: { data } }): Promise<void> => {
    this.associationModalTitle = 'Edit Association';
    this.associationFormData = this.associationFormService.getFormData(data);
    this.associationModalComponent.showModal();
  };

  onRowRemoving = (e): void => {
    e.cancel = this.agentAssociationsService.delete(this.agentId$.value, e.data[BaseModelKeys.dbId], this._sourceType);
  };

  handleSaveAssociation = async (e): Promise<void> => {
    const validationResults = await this.validateGroupRules();
    if (validationResults?.errors?.length) {
      return;
    }

    this.associationFormService.saveAssociation(this.agentId$.value, this._sourceType).then(() => {
      e.component.instance.hide();
    });
  };

  handleAssociationFormPopupClose = (): Promise<boolean> => {
    this.errors.set([]);
    return this.associationFormService.onCancelEdit();
  };

  calculateContactNumberDisplayValue = (association: Association) => matcher(association?.contact_number);

  calculateNameDisplayValue = (association: Association) => {
    return [association?.[AssociationKeys.firstName], association?.[AssociationKeys.lastName]]
      .filter(Boolean)
      .join(' ');
  };

  protected enableEditMode = () => {
    this.inEditMode.set(true);
  };

  protected disableEditMode = () => {
    this.inEditMode.set(false);
  };

  calculateFullAssociationsDisplayValue = (association: Association) => {
    return {
      fullName: this.calculateNameDisplayValue(association),
      contactNumber: this.calculateContactNumberDisplayValue(association),
      emailAddress: association?.[AssociationKeys.emailAddress],
      associationType: association?.[AssociationKeys.associationType],
      isEmergencyContact: association?.[AssociationKeys.isEmergencyContact],
    };
  };

  onGenderSelect = (item: Lookup): void => this.associationFormService.setSelectedGender(item);

  onRelationshipTypeSelectionChanged = (item: ActiveLookup): void =>
    this.associationFormService.setSelectedRelationshipType(item);

  protected onValidationSummaryItemClick = async (brokenRule: BrokenRule<AssociationFormSections>): Promise<void> => {
    this.associationsTabsComponent.selectedIndex = ASSOCIATION_FORM_TABS.findIndex(
      ({ id }) => id === brokenRule?.sectionId,
    );

    setTimeout(() => {
      brokenRule?.editor?.focus();
    }, 0);
  };

  protected validateGroupRules = async () => {
    const errors = [];
    const validationGroups = [
      this.mainInfoValidationGroup,
      this.funStuffSizesValidationGroup,
      this.addressValidationGroup,
      this.dietaryConsiderationsValidationGroup,
      this.flightPreferenceValidationGroup,
    ];

    for (const validationGroup of validationGroups) {
      const validationResults = await validateSingleDxGroup(validationGroup);

      if (validationResults.isValid) {
        continue;
      }
      validationResults?.invalidEditors?.forEach(editor => {
        const editorName = editor?.option('name') ? editor?.option('name') : editor?.option('elementAttr')?.id;
        const validationError = editor?.option('validationError');

        const brokenRule: BrokenRule<AssociationFormSections> = {
          editor,
          label: this._getBrokenRuleLabel(editorName, validationError?.message),
          sectionId: this._getBrokenRuleSectionId(validationGroup),
        };

        errors.push(brokenRule);
      });
    }
    this.errors.set(errors);

    return { errors };
  };

  private _getBrokenRuleSectionId(validationGroup: string): AssociationFormSections {
    const sectionMap: { [key: string]: AssociationFormSections } = {
      [this.funStuffSizesValidationGroup]: AssociationFormSections.funStuffSizes,
      [this.dietaryConsiderationsValidationGroup]: AssociationFormSections.dietaryConsiderations,
      [this.flightPreferenceValidationGroup]: AssociationFormSections.flightPreference,
      [this.addressValidationGroup]: AssociationFormSections.address,
    };

    return sectionMap[validationGroup] || AssociationFormSections.mainInformation;
  }

  private _getBrokenRuleLabel(editorName: string = '', message: string) {
    const [groupName] = editorName?.split('.');

    switch (groupName) {
      case AssociationKeys.contactNumber:
        return `Phone Number: ${message}`;

      case AssociationKeys.tShirtSizes:
        return `Fun Stuff Sizes: ${message}`;

      case AssociationKeys.dietaryConsideration:
        return `${message}`;

      case AssociationKeys.flightPreference:
        return `Preference Airline: ${message}`;

      default:
        return message;
    }
  }

  protected readonly AgentKeys = AgentKeys;

}
